import React from 'react';
import { COURSES, PASS_MARK } from '../data/academyCourses';
import { notifyTeam } from '../lib/notify';
import { track } from '../lib/analytics';
import type { AcademyState, Course, Learner, QuizResult } from '../types/academy';
import { courseProgress, dayStamp, lessonKey, quizKey } from '../utils/academyProgress';
const STORAGE_KEY = 'cavalla.academy.v1';
const REF_KEY = 'cavalla.academy.ref';
const EMPTY: AcademyState = {
  learner: null,
  enrolments: {},
  completions: {},
  quizzes: {},
  lastAccessed: {},
  activityDays: [],
  certificates: {}
};
interface JoinInput {
  name: string;
  email: string;
  country: string;
  organisation?: string;
}
interface AcademyContextValue {
  state: AcademyState;
  pendingReferral: string | null;
  join: (input: JoinInput) => Learner;
  signOut: () => void;
  enrol: (course: Course, justJoined?: Learner) => Promise<void>;
  isEnrolled: (slug: string) => boolean;
  completeLesson: (course: Course, moduleIndex: number, lessonIndex: number) => void;
  submitQuiz: (course: Course, moduleIndex: number, answers: Record<number, string>) => QuizResult;
  touch: (slug: string) => void;
}
const AcademyContext = React.createContext<AcademyContextValue | null>(null);
export function AcademyProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = React.useState<AcademyState>(readState);
  const [pendingReferral, setPendingReferral] = React.useState<string | null>(() => readRef());
  React.useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* progress still holds for this session */
    }
  }, [state]);

  // A ?ref= code on any Academy URL is kept until the visitor joins.
  React.useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref && /^[A-Z0-9]{6}$/i.test(ref)) {
      setPendingReferral(ref.toUpperCase());
      try {
        window.localStorage.setItem(REF_KEY, ref.toUpperCase());
      } catch {
        /* ignore */
      }
    }
  }, []);
  const join = React.useCallback((input: JoinInput): Learner => {
    const learner: Learner = {
      name: input.name.trim(),
      email: input.email.trim().toLowerCase(),
      country: input.country,
      organisation: input.organisation?.trim() || undefined,
      referralCode: makeCode(),
      referredBy: pendingReferral ?? undefined,
      joinedAt: new Date().toISOString()
    };
    setState(s => ({
      ...s,
      learner
    }));
    return learner;
  }, [pendingReferral]);
  const signOut = React.useCallback(() => setState(EMPTY), []);
  const enrol = React.useCallback(async (course: Course, justJoined?: Learner) => {
    const learner = justJoined ?? state.learner;
    if (!learner || state.enrolments[course.slug]) return;
    const now = new Date().toISOString();
    setState(s => ({
      ...s,
      enrolments: {
        ...s.enrolments,
        [course.slug]: now
      },
      lastAccessed: {
        ...s.lastAccessed,
        [course.slug]: now
      }
    }));
    track('academy_enrol', {
      source: course.slug,
      line: course.serviceLine
    });
    void notifyTeam(`Academy enrolment: ${learner.email} (${course.title})`, {
      Form: 'Cavalla Academy enrolment',
      Course: course.title,
      Name: learner.name,
      Email: learner.email,
      Country: learner.country,
      Organisation: learner.organisation,
      'Referred by code': learner.referredBy,
      'Enrolled at': new Date().toUTCString()
    });
  }, [state.learner, state.enrolments]);
  const isEnrolled = React.useCallback((slug: string) => Boolean(state.enrolments[slug]), [state.enrolments]);
  const completeLesson = React.useCallback((course: Course, moduleIndex: number, lessonIndex: number) => {
    setState(s => {
      const key = lessonKey(course.slug, moduleIndex, lessonIndex);
      if (s.completions[key]) return s;
      const today = dayStamp(new Date());
      const next: AcademyState = {
        ...s,
        completions: {
          ...s.completions,
          [key]: new Date().toISOString()
        },
        activityDays: s.activityDays.includes(today) ? s.activityDays : [...s.activityDays, today],
        lastAccessed: {
          ...s.lastAccessed,
          [course.slug]: new Date().toISOString()
        }
      };
      return withCertificate(course, next);
    });
  }, []);
  const submitQuiz = React.useCallback((course: Course, moduleIndex: number, answers: Record<number, string>): QuizResult => {
    const quiz = course.modules[moduleIndex].quiz ?? [];
    const correct = quiz.filter((q, i) => answers[i] === q.correctId).length;
    const score = quiz.length ? Math.round(correct / quiz.length * 100) : 100;
    const result: QuizResult = {
      score,
      passed: score >= PASS_MARK,
      attemptedAt: new Date().toISOString()
    };
    setState(s => {
      const key = quizKey(course.slug, moduleIndex);
      const previous = s.quizzes[key];
      // A pass is never undone by a later, lower attempt.
      const kept = previous?.passed && !result.passed ? previous : result;
      return withCertificate(course, {
        ...s,
        quizzes: {
          ...s.quizzes,
          [key]: kept
        }
      });
    });
    return result;
  }, []);
  const touch = React.useCallback((slug: string) => {
    setState(s => ({
      ...s,
      lastAccessed: {
        ...s.lastAccessed,
        [slug]: new Date().toISOString()
      }
    }));
  }, []);
  const value = React.useMemo(() => ({
    state,
    pendingReferral,
    join,
    signOut,
    enrol,
    isEnrolled,
    completeLesson,
    submitQuiz,
    touch
  }), [state, pendingReferral, join, signOut, enrol, isEnrolled, completeLesson, submitQuiz, touch]);
  return <AcademyContext.Provider value={value} data-id="element-1435" data-mp="e6z09kl4fysc">{children}</AcademyContext.Provider>;
}
export function useAcademy(): AcademyContextValue {
  const value = React.useContext(AcademyContext);
  if (!value) throw new Error('useAcademy must be used inside AcademyProvider');
  return value;
}
export function findCertificate(state: AcademyState, id: string, courses: Course[] = COURSES) {
  const cert = Object.values(state.certificates).find(c => c.id === id);
  if (!cert) return null;
  const course = courses.find(c => c.slug === cert.courseSlug);
  return course ? {
    cert,
    course
  } : null;
}
function withCertificate(course: Course, state: AcademyState): AcademyState {
  if (!state.learner || !state.enrolments[course.slug] || state.certificates[course.slug]) return state;
  if (!courseProgress(course, state).complete) return state;
  return {
    ...state,
    certificates: {
      ...state.certificates,
      [course.slug]: {
        id: makeUuid(),
        courseSlug: course.slug,
        learnerName: state.learner.name,
        issuedAt: new Date().toISOString()
      }
    }
  };
}
function readState(): AcademyState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? {
      ...EMPTY,
      ...(JSON.parse(raw) as Partial<AcademyState>)
    } : EMPTY;
  } catch {
    return EMPTY;
  }
}
function readRef(): string | null {
  try {
    return window.localStorage.getItem(REF_KEY);
  } catch {
    return null;
  }
}
function makeCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({
    length: 6
  }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}
function makeUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : r & 0x3 | 0x8).toString(16);
  });
}